/**
 * Tenant Navigation Sections
 *
 * Navigation configuration for TENANT users (client companies / CRM users)
 * Based on MODULES_CONFIG as Single Source of Truth
 *
 * User Types:
 * - platform: OWLY team members (internal administrators) - uses owly-sections.ts
 * - tenant: Client company users (external CRM users) - uses THIS file
 *
 * @scope tenant users
 * @generated-from /src/config/modules-config.ts
 * @see /docs/NAVIGATION-ARCHITECTURE.md
 */

import { NavigationSection } from '../types/sidebar.types';
import MODULES_CONFIG, { ModuleConfig } from '@/config/modules-config';
import {
  HomeIcon,
  BuildingOffice2Icon,
  FunnelIcon,
  UserGroupIcon,
  ChatBubbleLeftIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  SparklesIcon,
  BeakerIcon,
  UsersIcon,
  ChartBarSquareIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  FaceSmileIcon,
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BookOpenIcon,
  WrenchScrewdriverIcon as DevToolsIcon,
  DocumentChartBarIcon,
  ChartPieIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  BanknotesIcon,
  MapIcon,
  PhotoIcon,
  TrophyIcon,
  Cog6ToothIcon,
  XCircleIcon,
  ArrowPathIcon,
  BoltIcon,
  LinkIcon,
  // === NEW ICONS FOR UNIFIED MODULES ===
  Squares2X2Icon,
  QueueListIcon,
  EyeIcon,
  ShieldCheckIcon,
  ServerIcon,
  LifebuoyIcon,
  CodeBracketIcon,
  KeyIcon,
  CalculatorIcon,
  PlayIcon,
  SignalIcon,
  TicketIcon,
  HomeModernIcon,
  GlobeAmericasIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  FlagIcon,
  UserCircleIcon,
  FilmIcon,
  SwatchIcon,
  PaintBrushIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  RectangleStackIcon,
  Square3Stack3DIcon,
} from './icons';

// Icon mapping from string keys to actual icon components
const ICON_MAP: Record<string, typeof HomeIcon> = {
  HomeIcon,
  BuildingOffice2Icon,
  FunnelIcon,
  UserGroupIcon,
  ChatBubbleLeftIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  SparklesIcon,
  BeakerIcon,
  UsersIcon,
  ChartBarSquareIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  FaceSmileIcon,
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BookOpenIcon,
  DevToolsIcon,
  DocumentChartBarIcon,
  ChartPieIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  BanknotesIcon,
  MapIcon,
  PhotoIcon,
  TrophyIcon,
  Cog6ToothIcon,
  XCircleIcon,
  ArrowPathIcon,
  BoltIcon,
  LinkIcon,
  // === NEW ICONS FOR UNIFIED MODULES ===
  Squares2X2Icon,
  QueueListIcon,
  EyeIcon,
  ShieldCheckIcon,
  ServerIcon,
  LifebuoyIcon,
  CodeBracketIcon,
  KeyIcon,
  CalculatorIcon,
  PlayIcon,
  SignalIcon,
  TicketIcon,
  HomeModernIcon,
  GlobeAmericasIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  FlagIcon,
  UserCircleIcon,
  FilmIcon,
  SwatchIcon,
  PaintBrushIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  RectangleStackIcon,
  Square3Stack3DIcon,
};

/**
 * Get icon component from string key
 */
function getIcon(iconKey: string): typeof HomeIcon {
  return ICON_MAP[iconKey] || HomeIcon;
}

/**
 * Convert ModuleConfig to NavigationSection format
 */
function moduleToSection(module: ModuleConfig, leadCount: number = 0, clientCount: number = 0): NavigationSection {
  return {
    id: module.id,
    label: module.label,
    icon: getIcon(module.icon),
    scope: module.scope,
    owlyOnly: module.owlyOnly,
    platformTag: module.platformTag,
    requiredPermission: module.requiredPermission,
    items: module.items.map(item => ({
      id: item.id,
      label: item.label,
      globalLabel: item.globalLabel,
      projectLabel: item.projectLabel,
      icon: getIcon(item.icon),
      href: item.href,
      badge: getBadgeValue(item.id, leadCount, clientCount, item.badge),
      badgeVariant: item.badgeVariant,
      requiredPermission: item.requiredPermission,
      matchPaths: item.matchPaths,
      // === UNIFIED ARCHITECTURE ===
      scope: item.scope, // Item-level scope override
      platformTag: item.platformTag, // PLATFORM tag for sidebar
    })),
  };
}

/**
 * Get dynamic badge values for specific items
 * NOTE: IDs must match MODULES_CONFIG item IDs
 */
function getBadgeValue(
  itemId: string,
  leadCount: number,
  clientCount: number,
  defaultBadge?: string | number
): string | number | undefined {
  switch (itemId) {
    // Leads module - 'list' is the new ID for leads list
    case 'list':
      return undefined;
    // Gestion module - updated IDs
    case 'projects':
      return undefined; // Dynamic badge added in Sidebar.tsx
    case 'units':
      return undefined;
    // Communications module
    case 'omnichannel':
      return undefined;
    // Activities module - updated IDs
    case 'appointments':
      return undefined;
    case 'tasks':
      return undefined;
    default:
      return defaultBadge;
  }
}

/**
 * Create tenant navigation sections from MODULES_CONFIG
 * This is the main export that the sidebar uses for tenant users
 *
 * @param leadCount - Number of leads to display in badge
 * @param clientCount - Number of clients to display in badge
 * @returns NavigationSection[] - Array of navigation sections
 */
export const createTenantSections = (leadCount: number = 0, clientCount: number = 0): NavigationSection[] => {
  return MODULES_CONFIG.map(module => moduleToSection(module, leadCount, clientCount));
};

/**
 * @deprecated Use createTenantSections instead
 * Kept for backward compatibility during migration
 */
export const createCRMSections = createTenantSections;

// Export for backward compatibility
export default createTenantSections;
